import type { ReactNode } from "react";
import { createContext, useContext, useState, useCallback } from "react";
import type {
  FilterValue,
  FilterTypes,
  FilterProps
} from "../TableFilters.type";
import type { Column } from "../../Table.types";

type FilterOverlayContextType<TableData, FilterType extends FilterTypes> = {
  // Button states
  isSearchDisabled: boolean;
  isResetDisabled: boolean;

  // Filter values and state
  searchValue: FilterValue<FilterType>;
  setSearchValue: (value: FilterValue<FilterType>) => void;
  isManualFiltering: boolean;

  // Filter visibility
  isFilterVisible: boolean;
  setIsFilterVisible: (visible: boolean | ((prev: boolean) => boolean)) => void;

  // Column information
  activeColumn: Column<TableData>;
  label: string;
  filterProps?: FilterProps;

  // Actions
  handleSearch: () => void;
  handleReset: () => void;
  setButtonStates: (searchDisabled: boolean, resetDisabled: boolean) => void;
  closeFilter: () => void;
};

type FilterOverlayProviderProps<TableData, FilterType extends FilterTypes> = {
  children: ReactNode;
  column: Column<TableData>;
  onSearch: (value: FilterValue<FilterType>) => void;
  onReset: () => void;
  isManualFiltering: boolean;
};

const FilterContext = createContext<
  FilterOverlayContextType<any, any> | undefined
>(undefined);

/**
 * Provider component for managing all filter-related state and actions
 * Centralizes filter logic and reduces prop drilling throughout the table components
 */
export const FilterOverlayProvider = <
  TableData extends Record<string, any>,
  FilterType extends FilterTypes
>({
  children,
  column,
  isManualFiltering,
  onSearch,
  onReset
}: FilterOverlayProviderProps<TableData, FilterType>) => {
  // Button states
  const [isSearchDisabled, setIsSearchDisabled] = useState(false);
  const [isResetDisabled, setIsResetDisabled] = useState(false);

  // Get current filter value from the column
  const currentFilterValue = column.getFilterValue() as FilterValue<FilterType>;

  // Filter values and visibility
  const [searchValue, setSearchValue] = useState<FilterValue<FilterType>>();
  const [isFilterVisible, setIsFilterVisibleInternal] = useState(false);

  // Wrapper function to handle filter visibility changes
  const setIsFilterVisible = useCallback(
    (visible: boolean | ((prev: boolean) => boolean)) => {
      setIsFilterVisibleInternal(prev => {
        const isVisible =
          typeof visible === "function" ? visible(prev) : visible;

        // If transitioning from false to true, update search value from current filter
        if (isVisible && !prev) {
          setSearchValue(currentFilterValue);
        }

        return isVisible;
      });
    },
    [currentFilterValue]
  );

  // filter props
  const label = column.columnDef.header as string;
  const { filterProps } = column.columnDef.meta as {
    filterProps?: FilterProps;
  };

  const setButtonStates = useCallback(
    (searchDisabled: boolean, resetDisabled: boolean) => {
      setIsSearchDisabled(searchDisabled);
      setIsResetDisabled(resetDisabled);
    },
    []
  );

  const handleSearch = useCallback(() => {
    onSearch?.(searchValue);
    setIsFilterVisibleInternal(false);
  }, [searchValue, onSearch]);

  const handleReset = useCallback(() => {
    onReset?.();
    setSearchValue(undefined);
    setIsFilterVisibleInternal(false);
  }, [onReset]);

  const closeFilter = useCallback(() => {
    setIsFilterVisibleInternal(false);
    setSearchValue(undefined);
  }, []);

  return (
    <FilterContext.Provider
      value={{
        // Button states
        isSearchDisabled,
        isResetDisabled,

        // Filter values and state
        searchValue,
        setSearchValue,
        isManualFiltering,

        // Filter visibility
        isFilterVisible,
        setIsFilterVisible,

        // Column information
        activeColumn: column,
        label,
        filterProps,

        // Actions
        handleSearch,
        handleReset,
        setButtonStates,
        closeFilter
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};

export const useFilterOverlay = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error(
      "useFilterOverlay must be used within a FilterOverlayProvider"
    );
  }
  return context;
};
